import { useCallback, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Dashboard } from '@detect/shared';
import { dashboardService } from '../../services/dashboards';
import type { WithId } from '../../services/types';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useCursorList } from '../../hooks/useCursorList';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
} from '../../components/common';

type Scope = 'shared' | 'mine' | 'all';

export function DashboardsPage() {
  const { user } = useAuth();
  const { tenantId, hasRole } = useTenant();
  const [scope, setScope] = useState<Scope>('shared');
  const [showCreate, setShowCreate] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);

  const fetchPage = useCallback(
    (tid: string, startAfter: string | undefined) =>
      dashboardService.list(tid, { scope, limit: 50, startAfter }),
    [scope]
  );
  const list = useCursorList<WithId<Dashboard>>(fetchPage, scope);
  const importInputRef = useRef<HTMLInputElement | null>(null);

  /** JSON import (#10): the file carries the CREATE-input shape the
   *  export produces; the API's strict schema is the real validator —
   *  client-side we only ensure it parses and is an object. */
  const importJson = async (file: File) => {
    if (!tenantId) return;
    setActionError(null);
    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error('Not valid JSON.');
      }
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('Expected a dashboard export object.');
      }
      await dashboardService.create(
        tenantId,
        parsed as Parameters<typeof dashboardService.create>[1]
      );
      setScope('mine'); // imports are created as the caller's dashboard
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  const clone = async (d: WithId<Dashboard>) => {
    if (!tenantId) return;
    setActionError(null);
    try {
      await dashboardService.clone(tenantId, d.id);
      setScope('mine'); // clones are private to the caller
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  const remove = async (d: WithId<Dashboard>) => {
    if (!tenantId) return;
    if (!window.confirm(`Delete dashboard ${d.name}?`)) return;
    setActionError(null);
    try {
      await dashboardService.remove(tenantId, d.id);
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Dashboards</h1>
        <div className="page-actions">
          <label htmlFor="dashboard-scope" className="visually-hidden">
            Dashboard scope
          </label>
          <select
            id="dashboard-scope"
            name="scope"
            value={scope}
            onChange={(e) => setScope(e.target.value as Scope)}
          >
            <option value="shared">Shared</option>
            <option value="mine">Mine</option>
            {hasRole('admin') && <option value="all">All (admin)</option>}
          </select>
          {hasRole('agent') && (
            <>
              <input
                ref={importInputRef}
                type="file"
                accept="application/json,.json"
                className="visually-hidden"
                aria-label="Import dashboard JSON"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) void importJson(file);
                }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => importInputRef.current?.click()}
              >
                Import JSON
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowCreate(true)}
              >
                New dashboard
              </button>
            </>
          )}
        </div>
      </div>
      {actionError !== null && <ErrorState error={actionError} />}

      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState
          title="No dashboards"
          description="Create a dashboard and add graph, NOC, and alert widgets."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Visibility</th>
                <th>Widgets</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {list.items.map((d) => (
                <tr key={d.id}>
                  <td>
                    <Link to={`/dashboards/${d.id}`}>{d.name}</Link>
                  </td>
                  <td>{d.shared ? 'shared' : 'private'}</td>
                  <td>{d.widgets.length}</td>
                  <td className="row-actions">
                    {hasRole('agent') && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => void clone(d)}
                      >
                        Clone
                      </button>
                    )}
                    {((d.ownerUserId === user?.id && hasRole('agent')) ||
                      hasRole('admin')) && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => void remove(d)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMoreError={list.loadMoreError}
        onLoadMore={() => void list.loadMore()}
      />
      {showCreate && (
        <CreateDashboardModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void list.reload();
          }}
        />
      )}
    </div>
  );
}

function CreateDashboardModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { tenantId } = useTenant();
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setSubmitting(true);
    setError(null);
    try {
      await dashboardService.create(tenantId, { name, shared });
      onCreated();
    } catch (err) {
      setError(err);
      setSubmitting(false);
    }
  };

  return (
    <Modal title="New dashboard" onClose={onClose}>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="dash-name">Name</label>
          <input
            id="dash-name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-field form-field-inline">
          <input
            id="dash-shared"
            name="shared"
            type="checkbox"
            checked={shared}
            onChange={(e) => setShared(e.target.checked)}
          />
          <label htmlFor="dash-shared">Shared with the whole tenant</label>
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create'}
        </button>
      </form>
    </Modal>
  );
}
